import { Injectable, inject } from '@angular/core';
import { CategoriaGrupo, IListado, IPopulate, IQueryParam } from 'modelos/src';
import { IColumnas } from '../../../../auxiliares/tabla/tabla.component';
import { IRegExpSearch } from '../../../../auxiliares/tabla/filtro/filtro.component';
import { LuminariasHttpService } from '../../../../auxiliares/servicios/http/luminarias.service';
import { PuestasService } from '../../../../auxiliares/servicios/http/puestas.service';

/**
 * Config de cómo listar/asignar las entidades de una categoría de grupo. Es el
 * mapa central `categoria → entidad` que antes estaba hardcodeado y disperso
 * (switch de listado-grupos + `categoria="..."` de cada form). El asignador
 * genérico sólo mira `categoria` y usa esta config: no conoce luminaria/puesta.
 *
 * `fkMulti`: true = la entidad tiene un ARRAY de grupos (`idsGrupos`) y se
 * asigna con add/remove sin pisar; false = FK simple (`idGrupo`) que se
 * reemplaza (mover). Hoy sólo Luminaria y Puesta están cableadas (ambas multi);
 * el resto se agrega sumando una entrada acá, sin tocar el componente.
 */
export interface ConfigAsignacionEntidad {
  /** Nombre para UI (plural): "luminarias", "puestas". */
  etiqueta: string;
  campoFk: 'idsGrupos' | 'idGrupo';
  fkMulti: boolean;
  /** getFiltered de la entidad. */
  listar: (q: IQueryParam) => Promise<IListado<Record<string, unknown>>>;
  /** update parcial de la entidad. */
  actualizar: (id: string, patch: Record<string, unknown>) => Promise<unknown>;
  /** Filtro fijo extra (ej. { categoria: 'Vehículo' } para activos Flota). */
  filtroBase?: Record<string, unknown>;
  /** Campos mínimos a traer (space-separated). Siempre incluir _id y el FK. */
  select: string;
  /** Columnas mínimas específicas de la entidad (identificación + 1-2 datos). */
  columnasMin: IColumnas<any>[];
  /** Campos donde busca el search de la tabla. */
  search: IRegExpSearch;
  /** Populate para traer datos que la tabla necesite. */
  populate?: IPopulate[];
  /** Ids de grupos que la entidad ya tiene (misma categoría). */
  gruposDe: (e: Record<string, unknown>) => string[];
  etiquetaEntidad: (e: Record<string, unknown>) => string;
  geoField?: string;
}

@Injectable({ providedIn: 'root' })
export class GrupoEntidadRegistry {
  private luminarias = inject(LuminariasHttpService);
  private puestas = inject(PuestasService);

  private readonly registro: Partial<
    Record<CategoriaGrupo, ConfigAsignacionEntidad>
  > = {
    Luminaria: {
      etiqueta: 'luminarias',
      campoFk: 'idsGrupos',
      fkMulti: true,
      listar: (q) => this.luminarias.getFiltered(q) as Promise<IListado<any>>,
      actualizar: (id, patch) => this.luminarias.update(id, patch),
      select: '_id identificacion deveui idsGrupos',
      geoField: 'ubicacion',
      columnasMin: [
        {
          header: {
            label: 'Identificación',
            sortable: true,
            sort: 'identificacion',
          },
          row: { field: 'identificacion' },
        },
        {
          header: { label: 'DevEUI', sortable: true, sort: 'deveui' },
          row: { field: 'deveui' },
        },
      ],
      search: { fields: ['identificacion', 'deveui'] },
      gruposDe: (e) => (e['idsGrupos'] as string[]) ?? [],
      etiquetaEntidad: (e) => {
        const id = (e['identificacion'] as string) || 'Sin identificación';
        const dev = e['deveui'] as string;
        return dev ? `${id} · ${dev}` : id;
      },
    },
    Puesta: {
      etiqueta: 'puestas',
      campoFk: 'idsGrupos',
      fkMulti: true,
      listar: (q) => this.puestas.getFiltered(q) as Promise<IListado<any>>,
      actualizar: (id, patch) => this.puestas.update(id, patch),
      select: '_id identificacion idsGrupos',
      geoField: 'ubicacion',
      columnasMin: [
        {
          header: {
            label: 'Identificación',
            sortable: true,
            sort: 'identificacion',
          },
          row: { field: 'identificacion' },
        },
      ],
      search: { fields: ['identificacion'] },
      gruposDe: (e) => (e['idsGrupos'] as string[]) ?? [],
      etiquetaEntidad: (e) =>
        (e['identificacion'] as string) || 'Sin identificación',
    },
    // Pendientes (cardinalidad a confirmar): 'Activo'/'Flota'/'Línea de
    // colectivo' → Activo.idGrupo (single) con filtroBase por activo.categoria;
    // 'Normal'. Se agregan aquí cuando se definan, sin tocar el componente.
  };

  public get(categoria?: CategoriaGrupo): ConfigAsignacionEntidad | undefined {
    return categoria ? this.registro[categoria] : undefined;
  }

  public soportada(categoria?: CategoriaGrupo): boolean {
    return !!this.get(categoria);
  }

  /** ¿La categoría permite asignación por polígono (entidad fija con geoField)? */
  public soportaGeo(categoria?: CategoriaGrupo): boolean {
    return !!this.get(categoria)?.geoField;
  }
}
